import { useRef, useState, useCallback, useEffect } from "react";

export default function BeforeAfterSlider({ before, after, alt = "Vorher-Nachher-Vergleich" }) {
  const [pos, setPos] = useState(50);
  const [width, setWidth] = useState(0);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    setWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };

  return (
    <div className="rounded-md border-4 border-anthracite bg-anthracite p-2">
      <div
        ref={containerRef}
        className="relative aspect-[4/3] w-full cursor-ew-resize select-none overflow-hidden rounded-sm"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        data-testid="before-after-slider"
      >
        {/* After (base) */}
        <img
          src={after}
          alt={`${alt} – Nachher`}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        <span className="absolute right-3 top-3 z-10 rounded-sm bg-yellow px-2.5 py-1 text-xs font-bold text-brand-black">
          Nachher
        </span>

        {/* Before (clipped) */}
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img
            src={before}
            alt={`${alt} – Vorher`}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ width: width ? `${width}px` : "100%", maxWidth: "none" }}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
          <span className="absolute left-3 top-3 z-10 rounded-sm bg-brand-black/80 px-2.5 py-1 text-xs font-bold text-white">
            Vorher
          </span>
        </div>

        {/* Handle */}
        <div
          className="absolute inset-y-0 z-20 w-0.5 bg-yellow"
          style={{ left: `${pos}%` }}
        >
          <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-yellow text-brand-black shadow-lg">
            <span className="text-xs font-bold">↔</span>
          </div>
        </div>
      </div>

      {/* Accessible / no-JS fallback control */}
      <label className="sr-only" htmlFor="ba-range">
        Vergleich Vorher-Nachher
      </label>
      <input
        id="ba-range"
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="mt-2 w-full accent-yellow"
        aria-label="Vorher-Nachher-Vergleich anpassen"
        data-testid="before-after-range"
      />
    </div>
  );
}
